import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2 } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Panel } from "@/components/admin/ui";
import { Switch } from "@/components/ui/switch";

const TOGGLES = [
  ["online_enabled", "Online payments enabled", "Master switch for all online card payments."],
  ["portal_enabled", "Guest Portal payments enabled", "Allow guests to pay from their portal."],
  ["frontdesk_enabled", "Front Desk payment requests", "Allow authorized staff to create secure payment requests."],
  ["deposits_enabled", "Deposits enabled", "Allow guests to pay the required deposit online."],
  ["event_enabled", "Event payments enabled", "Allow private-event deposits and balances."],
  ["membership_enabled", "Membership payments enabled", "Allow membership payments."],
];

export default function OnlinePaymentSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/payments/online-settings").then((r) => setS(r.data)).catch(() => setS(false));
  useEffect(() => { load(); }, []);
  if (s === null) return null;
  if (s === false) return null;

  const toggle = async (k, v) => {
    setS((p) => ({ ...p, [k]: v }));
    setSaving(true);
    try { await api.put("/admin/payments/online-settings", { [k]: v }); toast.success("Payment setting updated."); }
    catch (e) { toast.error(apiErr(e)); load(); } finally { setSaving(false); }
  };

  const conn = s.connection || {};
  const pillCls = conn.state === "connected"
    ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
    : "text-muted-foreground border-white/20 bg-white/5";

  return (
    <Panel title="Online Payment Settings">
      <div className="flex items-center justify-between py-2 mb-3 border-b border-white/5" data-testid="online-payment-status">
        <div className="flex items-center gap-2"><CreditCard size={16} className="text-gold" /><div><p className="text-sm">Connection status</p><p className="text-xs text-muted-foreground">Currency: {s.currency}{conn.webhook_ready ? " · Webhook active" : ""}</p></div></div>
        <span className={`status-pill border ${pillCls}`}>{conn.label}</span>
      </div>
      <div className="space-y-3">
        {TOGGLES.map(([k, label, desc]) => (
          <div key={k} className="flex items-center justify-between">
            <div><p className="text-sm">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
            <Switch checked={!!s[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`online-toggle-${k}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-white/5 text-xs" data-testid="online-payment-meta">
        <div className="flex justify-between"><span className="text-muted-foreground">Webhook status</span><span>{s.webhook_status}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Refund permissions</span><span>{s.refund_permissions}</span></div>
        <div className="flex justify-between col-span-2"><span className="text-muted-foreground">Default deposit rule</span><span className="text-right">{s.deposit_rule}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Request expiration</span><span>{s.request_expiration_days} days</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Currency</span><span>{s.currency}</span></div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/5 text-xs">
        <div><p className="text-muted-foreground">Last payment</p><p>{s.last_payment_at ? fmtDate(s.last_payment_at) : "—"}</p></div>
        <div><p className="text-muted-foreground">Last successful</p><p>{s.last_success_at ? fmtDate(s.last_success_at) : "—"}</p></div>
        <div><p className="text-muted-foreground">Last failed</p><p>{s.last_failed_at ? fmtDate(s.last_failed_at) : "—"}</p></div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">Sandbox Mode is active. Live activation requires production domain, HTTPS, verified webhooks and Owner approval. All credentials are stored server-side only.</p>
    </Panel>
  );
}
